"use client"

import { useState, useEffect } from "react"
import { useAuth, useUser } from "@clerk/nextjs"
import { usePathname } from "next/navigation"
import { MessageCircle, X, Loader2, CheckCircle2 } from "lucide-react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import ocularRequest from "@/service/request"
import posthog from "@/lib/posthog"

// ── Types ────────────────────────────────────────────────────────────────────

type FeedbackType = "bug" | "feature" | "question" | "other"
type WidgetState = "idle" | "open" | "submitting" | "success" | "error"

const TYPE_CHIPS: { value: FeedbackType; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature" },
  { value: "question", label: "Question" },
  { value: "other", label: "Other" },
]

const DESCRIPTION_PLACEHOLDERS: Record<FeedbackType, string> = {
  bug: "Steps to reproduce: 1. Go to... 2. Click... 3. See error",
  feature: "What would this unlock for you? What's the workaround today?",
  question: "What would you like to know?",
  other: "Tell us what's on your mind...",
}

// ── Metadata helper ───────────────────────────────────────────────────────────

function captureClientMetadata(pathname: string): Record<string, any> {
  const meta: Record<string, any> = {
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    locale: navigator.language,
    app_version: process.env.NEXT_PUBLIC_APP_VERSION || "dev",
  }

  // Extract video_id from /transcript/[id] or /videos/[id]
  const videoMatch = pathname.match(/\/(?:transcript|videos)\/([a-zA-Z0-9_-]+)/)
  if (videoMatch) meta.video_id = videoMatch[1]

  // Extract clip_id from /clips/[id]
  const clipMatch = pathname.match(/\/clips\/([a-zA-Z0-9_-]+)/)
  if (clipMatch) meta.clip_id = clipMatch[1]

  return meta
}

// ── Widget ────────────────────────────────────────────────────────────────────

export function FeedbackWidget() {
  const { isSignedIn, isLoaded, getToken } = useAuth()
  const { user } = useUser()
  const pathname = usePathname()

  const [widgetState, setWidgetState] = useState<WidgetState>("idle")
  const [showLabel, setShowLabel] = useState(false)

  // Form state
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("bug")
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [errorMsg, setErrorMsg] = useState("")

  // Hide on /admin/** routes, and in the clip editor where it sat on top of the tools rail
  const isHiddenRoute = pathname?.startsWith("/admin") ||/^\/clips\/[^/]+\/edit\//.test(pathname ?? "")

  // Reset form when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setWidgetState("idle")
      setTitle("")
      setDescription("")
      setFeedbackType("bug")
      setErrorMsg("")
    } else {
      setWidgetState("open")
    }
  }

  const isFormValid =
    title.length >= 3 && description.length >= 10 && feedbackType !== undefined

  const handleSubmit = async () => {
    if (!isFormValid) return
    setWidgetState("submitting")
    setErrorMsg("")

    try {
      const token = await getToken()
      const pageUrl = window.location.pathname + window.location.search
      const clientMetadata = captureClientMetadata(pathname || "")

      const payload = {
        feedback_type: feedbackType,
        title,
        description,
        page_url: pageUrl,
        client_metadata: clientMetadata,
      }

      const res = await ocularRequest("POST", "/feedback/", payload, {}, false, token)
      const data = res.data

      // Fire PostHog event
      posthog.capture("feedback_submitted", {
        feedback_id: data.id,
        feedback_type: feedbackType,
        page_url: pageUrl,
        has_video_id: !!clientMetadata.video_id,
        has_clip_id: !!clientMetadata.clip_id,
        title_length: title.length,
        description_length: description.length,
        app_version: clientMetadata.app_version,
      })

      setWidgetState("success")

      // Auto-close after 2.5s
      setTimeout(() => {
        setWidgetState("idle")
        setTitle("")
        setDescription("")
        setFeedbackType("bug")
      }, 2500)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      let msg = "Something went wrong. Please try again."
      if (typeof detail === "string") msg = detail
      else if (Array.isArray(detail)) msg = detail[0]?.msg || msg
      setErrorMsg(msg)
      setWidgetState("error")
    }
  }

  // Don't render if not authenticated, on admin pages or in the editor
  if (!isLoaded || !isSignedIn || isHiddenRoute) return null

  const isDialogOpen = widgetState === "open" || widgetState === "submitting" || widgetState === "success" || widgetState === "error"
  const isSubmitting = widgetState === "submitting"
  const isSuccess = widgetState === "success"

  return (
    <>
      {/* FAB button */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2">
        {/* Hover label */}
        {showLabel && !isDialogOpen && (
          <span className="rounded-md bg-foreground/90 px-2.5 py-1 text-xs font-medium text-background shadow-md animate-in fade-in slide-in-from-right-2 duration-200">
            Send feedback
          </span>
        )}
        <button
          onClick={() => setWidgetState("open")}
          onMouseEnter={() => setShowLabel(true)}
          onMouseLeave={() => setShowLabel(false)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
          aria-label="Send feedback"
        >
          <MessageCircle className="h-5 w-5" />
        </button>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md bg-background border border-white/[0.08] shadow-2xl p-0 overflow-hidden">
          <div className="p-6">
            {/* Header */}
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-foreground">Send us feedback</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                We read every piece. Really.
              </p>
            </div>

            {isSuccess ? (
              /* Success state */
              <div className="flex flex-col items-center py-6 gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                </div>
                <p className="text-sm font-medium text-foreground">Thanks — we'll review your feedback shortly.</p>
                {user?.emailAddresses?.[0]?.emailAddress && (
                  <p className="text-xs text-muted-foreground">
                    We'll follow up at{" "}
                    <span className="font-medium">{user.emailAddresses[0].emailAddress}</span>{" "}
                    if needed.
                  </p>
                )}
              </div>
            ) : (
              /* Form */
              <div className="space-y-4">
                {/* Type chips */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                    Type
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {TYPE_CHIPS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => setFeedbackType(value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          feedbackType === value
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-transparent text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                    Title <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, 200))}
                    placeholder="What happened in one line?"
                    disabled={isSubmitting}
                    maxLength={200}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 disabled:opacity-50 transition-colors"
                  />
                  {title.length > 0 && title.length < 3 && (
                    <p className="mt-1 text-xs text-destructive">Minimum 3 characters</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                    Description <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, 10000))}
                    placeholder={DESCRIPTION_PLACEHOLDERS[feedbackType]}
                    disabled={isSubmitting}
                    rows={4}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 disabled:opacity-50 transition-colors resize-none"
                  />
                  {description.length > 0 && description.length < 10 && (
                    <p className="mt-1 text-xs text-destructive">Minimum 10 characters</p>
                  )}
                </div>

                {/* Error */}
                {widgetState === "error" && errorMsg && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
                    {errorMsg}
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!isFormValid || isSubmitting}
                    onClick={handleSubmit}
                    className="min-w-[80px]"
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
